'use client';

import { useRouter } from 'next/navigation';
import { memo, useState } from 'react';

import { useBootstrapAuth } from '@/features/auth/hooks/useBootstrapAuth';
import { useReactivateArchivedWorkspaceMutation } from '@/features/workspaces/api/workspacesApi';
import { setCurrentWorkspace } from '@/features/workspaces/slices/workspacesSlice';
import { useAppDispatch } from '@/store/hooks';
import {
  Avatar,
  Badge,
  MainButton,
  SimpleTable,
  Tooltip,
  type SimpleTableColumn,
} from '@web/ui';
import { getDateTimeFormat, getErrorMessage, notify } from '@web/utils';

import type { ArchivedWorkspaceItem } from '@/features/workspaces/types';
import type { Workspace } from '@web/types';

type ReactivatedWorkspace = Pick<Workspace, 'name' | 'slug'>;

type Props = {
  items: ArchivedWorkspaceItem[];
  canReactivate: boolean;
  isLoading?: boolean;
  onReactivated?: (workspace: ReactivatedWorkspace) => void;
};

export const ArchivedWorkspacesTable = memo(function ArchivedWorkspacesTable({
  items,
  canReactivate,
  isLoading = false,
  onReactivated,
}: Props) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const bootstrapAuth = useBootstrapAuth();
  const [reactivateArchivedWorkspace, { isLoading: isReactivating }] =
    useReactivateArchivedWorkspaceMutation();
  const [activeId, setActiveId] = useState<string | null>(null);

  const handleReactivate = async (workspace: ArchivedWorkspaceItem) => {
    setActiveId(workspace.id);
    try {
      const response = await reactivateArchivedWorkspace(workspace.id).unwrap();
      const responseData = response.data;
      const reactivatedWorkspace = responseData?.workspace ?? null;

      if (!response.success || !reactivatedWorkspace) {
        throw new Error(response.message || 'Unable to reactivate workspace.');
      }

      dispatch(setCurrentWorkspace(reactivatedWorkspace));

      if (responseData?.user) {
        bootstrapAuth({
          currentUser: responseData.user,
          currentWorkspaceSlug: reactivatedWorkspace.slug,
          isWorkspaceSetupRequired:
            responseData.is_workspace_setup_required ?? false,
          isWorkspaceOnboarded:
            reactivatedWorkspace.is_workspace_onboarded ?? null,
        });
      }

      notify({
        type: 'success',
        message: response.message || 'Workspace reactivated.',
      });
      onReactivated?.(reactivatedWorkspace);
      router.push(`/${reactivatedWorkspace.slug}`);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setActiveId(null);
    }
  };

  const columns: Array<SimpleTableColumn<ArchivedWorkspaceItem>> = [
    {
      header: 'Workspace',
      render: (workspace) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={workspace.name} src={workspace.avatar_url || ''} />
          <span style={{ fontWeight: 600 }}>{workspace.name}</span>
        </div>
      ),
    },
    {
      header: 'Archived',
      render: (workspace) =>
        getDateTimeFormat(workspace.archived_at, 'Not available'),
    },
    {
      header: 'Reason',
      render: (workspace) => workspace.archived_reason || '-',
    },
    {
      header: 'Status',
      render: (workspace) => (
        <Badge label={workspace.status} tone="warning" size="sm" />
      ),
    },
    {
      header: '',
      align: 'right',
      width: 160,
      render: (workspace) => {
        const disabled = !canReactivate || !workspace.can_reactivate;

        return (
          <Tooltip title="Reactivate this workspace on the free plan and take you there.">
            <span>
              <MainButton
                ariaLabel={`Reactivate ${workspace.name}`}
                disabled={disabled}
                isLoading={isReactivating && activeId === workspace.id}
                onClick={() => {
                  void handleReactivate(workspace);
                }}
              >
                Reactivate
              </MainButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  if (!items?.length && !isLoading) {
    return null;
  }

  return (
    <SimpleTable
      title="Archived Workspaces"
      columns={columns}
      data={items}
      loading={isLoading}
      getRowKey={(workspace) => workspace.id}
    />
  );
});

ArchivedWorkspacesTable.displayName = 'ArchivedWorkspacesTable';
